import { Component, FormEvent } from 'react';
import cx from 'classnames';
import styles from '../../styles/plan/estimateReturnCalculator.scss';
import f2sInvestStyles from '../../styles/whatsapp-confirmation/index.scss';
import {
  calculateSipFutureValue,
  formatRupeeLiteral,
  Direction,
  calculateLumpsumFutureValue,
} from '../../constants';

interface Props {
  bucketDetails: any;
  invAmount: number;
}

interface State {
  amount: number;
  year: number;
  bar1Width: number;
  bar2Width: number;
  bar3Width: number;
  fixDeposit: number;
  ppf: number;
  saveTaxes: number;
  fundReturnRate: number;
  investmentType: string;
}

class EstimateReturnCalculator extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      amount: props.invAmount,
      year: 5,
      bar1Width: 0,
      bar2Width: 0,
      bar3Width: 100,
      fixDeposit: null,
      ppf: null,
      saveTaxes: null,
      fundReturnRate:
        props.bucketDetails.sections.planSummary.returns.return5yr,
      investmentType: Direction.SIP,
    };
  }

  private updateGraph = () => {
    const { year, fundReturnRate, amount, investmentType } = this.state;
    const amountCalculator =
      investmentType === Direction.SIP
        ? calculateSipFutureValue
        : calculateLumpsumFutureValue;

    const fixDeposit = Math.floor(amountCalculator(amount, year, 7));
    const ppf = Math.floor(amountCalculator(amount, year, 8));
    const saveTaxes = Math.floor(
      amountCalculator(amount, year, fundReturnRate)
    );
    const bar1Width = (fixDeposit / saveTaxes) * 100;
    const bar2Width = (ppf / saveTaxes) * 100;

    this.setState({
      bar1Width,
      bar2Width,
      fixDeposit,
      ppf,
      saveTaxes,
    });
  };

  private handleAmountChange = (e: any) => {
    const value = parseInt(e.target.value.replace(/\D/g, ''), 10) || 0;
    if (value < 99999999) {
      this.setState(
        {
          amount: value,
        },
        this.updateGraph
      );
    }
  };

  private handleYearChange = (e: any) => {
    const value = parseInt(e.target.value, 10);
    const returnKey = value >= 5 ? `return5yr` : `return${value}yr`;
    const {
      bucketDetails: {
        sections: {
          planSummary: {
            returns: { [returnKey]: fundReturnRate },
          },
        },
      },
    } = this.props;
    this.setState(
      {
        year: value,
        fundReturnRate,
      },
      this.updateGraph
    );
  };

  public componentDidMount() {
    this.updateGraph();
  }

  private handleInvestmentTypeChange = (e: FormEvent) => {
    const { value } = e.target;
    this.setState(
      {
        investmentType: value,
      },
      this.updateGraph
    );
  };

  private renderInvestmentPeriod() {
    const { year } = this.state;
    const yearsArr = [3, 5, 10, 15];
    return yearsArr.map((yr, index) => {
      return (
        <button
          type="button"
          className={cx(
            'btn btn-small text--uppercase text--md',
            f2sInvestStyles['btn'],
            f2sInvestStyles['small'],
            {
              [f2sInvestStyles['selected']]: year === yr,
            }
          )}
          name="year"
          value={yr}
          onClick={this.handleYearChange}
          key={index}
        >
          {yr}y
        </button>
      );
    });
  }

  public render() {
    const {
      bar1Width,
      bar2Width,
      bar3Width,
      fixDeposit,
      ppf,
      saveTaxes,
      amount,
      investmentType,
    } = this.state;
    const {
      bucketDetails: {
        sections: { title },
      },
    } = this.props;
    return (
      <div>
        <div className="row mb10">
          <div className="columns medium-3 mb20 pr0">
            <div className="font--dark-grey mb10">I want to invest</div>
            <div className={cx(styles['amount-container'])}>
              <div className="mb5">
                <span className={cx(styles['span'], 'font--faded')}>₹</span>
                <input
                  className={cx(styles['select-amount-input'])}
                  type="text"
                  value={amount.toLocaleString('en-IN')}
                  onChange={this.handleAmountChange}
                />
              </div>
            </div>
          </div>
          <div className="columns medium-4 mb20">
            <div className={cx('font--dark-grey mb10')}>Payment mode</div>
            <div
              className={cx(f2sInvestStyles['form-btn-group'], 'flex-1-0-0')}
              role="group"
            >
              <button
                type="button"
                className={cx(
                  'btn btn-small text--uppercase text--md',
                  f2sInvestStyles['btn'],
                  f2sInvestStyles['small'],
                  {
                    [f2sInvestStyles['selected']]:
                      investmentType === Direction.SIP,
                  }
                )}
                name="investmentType"
                value={Direction.SIP}
                onClick={this.handleInvestmentTypeChange}
              >
                Monthly
              </button>
              <button
                type="button"
                className={cx(
                  'btn btn-small text--uppercase text--md',
                  f2sInvestStyles['btn'],
                  f2sInvestStyles['small'],
                  {
                    [f2sInvestStyles['selected']]:
                      investmentType === Direction.LUMPSUM,
                  }
                )}
                name="investmentType"
                value={Direction.LUMPSUM}
                onClick={this.handleInvestmentTypeChange}
              >
                One Time
              </button>
            </div>
          </div>
          <div className="columns medium-5 mb20">
            <div className={cx('font--dark-grey mb10')}>No. of years</div>
            <div
              className={cx(f2sInvestStyles['form-btn-group'], 'flex-1-0-0')}
              role="group"
            >
              {this.renderInvestmentPeriod()}
            </div>
          </div>
        </div>

        <div className="font--dark-grey mb20">
          Estimated Value (based on historical returns)
        </div>

        <div>
          <section className="mb20">
            <div className="mb5 mobile-only text--normal">
              ClearTax {title} Plan
            </div>
            <div className={cx(styles['c-row'])}>
              <div
                className={styles['chart-bar']}
                style={{ width: `${bar3Width}%` }}
              >
                <span className="desktop-only">ClearTax {title} Plan</span>
                <span className="mobile-only">
                  ₹{formatRupeeLiteral(saveTaxes)}
                </span>
              </div>
              <div
                className={cx(
                  styles['ct-plan'],
                  'desktop-only black-text bold no-wrap'
                )}
              >
                ₹{formatRupeeLiteral(saveTaxes)}
              </div>
            </div>
          </section>

          <section className="mb20">
            <div className="mb5 mobile-only text--normal">
              Public Provident Fund
            </div>
            <div className={cx(styles['c-row'])}>
              <div
                className={cx(styles['chart-bar'], styles['min-width-35'])}
                style={{ width: `${bar2Width}%` }}
              >
                <span className="desktop-only">Public Provident Fund</span>
                <span className="mobile-only">₹{formatRupeeLiteral(ppf)}</span>
              </div>
              <div className={cx(styles['other-plan'], 'desktop-only no-wrap')}>
                ₹{formatRupeeLiteral(ppf)}
              </div>
            </div>
          </section>

          <section className="mb20">
            <div className="mb5 mobile-only text--normal">Fixed Deposit</div>
            <div className={cx(styles['c-row'])}>
              <div
                className={cx(styles['chart-bar'], styles['min-width-25'])}
                style={{ width: `${bar1Width}%` }}
              >
                <span className="desktop-only">Fixed Deposit</span>
                <span className="mobile-only">
                  ₹{formatRupeeLiteral(fixDeposit)}
                </span>
              </div>
              <div className={cx(styles['other-plan'], 'desktop-only no-wrap')}>
                ₹{formatRupeeLiteral(fixDeposit)}
              </div>
            </div>
          </section>
        </div>
        <div className="font--fadedgrey mt5 text--sm">
          Average interest rates are calculated at 7% for Tax free FD, 8% for
          PPF, 15.2% for mutual funds.
        </div>
      </div>
    );
  }
}

export default EstimateReturnCalculator;



// WEBPACK FOOTER //
// ./components/plan/estimateReturnCalculator.tsx